import React, { memo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Target, Play, Check, Trash2, Calendar, Zap, Timer, Bell, Music, Volume2, MapPin, Sparkles } from 'lucide-react';
import { Z_INDEX } from '../../constants/zIndex';
import { haptic } from '../../shared/utils/haptics';

// ─── Palette catégories ─────────────────────────────────────────────────────
const CAT = {
  vital:    { label: 'Vital',      emoji: '🔴', text: '#f87171', dot: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.18)', glow: 'rgba(239,68,68,0.06)', grad: ['rgba(239,68,68,0.12)','rgba(14,14,20,1)'] },
  chore:    { label: 'Corvée',     emoji: '🟠', text: '#fbbf24', dot: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.18)', glow: 'rgba(245,158,11,0.06)', grad: ['rgba(245,158,11,0.12)','rgba(14,14,20,1)'] },
  pleasure: { label: 'Plaisir',    emoji: '🟢', text: '#34d399', dot: '#10b981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.18)', glow: 'rgba(16,185,129,0.06)', grad: ['rgba(16,185,129,0.12)','rgba(14,14,20,1)'] },
  reward:   { label: 'Récompense', emoji: '🟣', text: '#a78bfa', dot: '#8b5cf6', bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.18)', glow: 'rgba(139,92,246,0.06)', grad: ['rgba(139,92,246,0.12)','rgba(14,14,20,1)'] },
  work:     { label: 'Travail',    emoji: '🔵', text: '#60a5fa', dot: '#3b82f6', bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.18)', glow: 'rgba(59,130,246,0.06)', grad: ['rgba(59,130,246,0.12)','rgba(14,14,20,1)'] },
  default:  { label: 'Tâche',      emoji: '⚪', text: '#a1a1aa', dot: '#71717a', bg: 'rgba(255,255,255,0.03)', border: 'rgba(255,255,255,0.08)', glow: 'rgba(0,0,0,0)', grad: ['rgba(255,255,255,0.04)','rgba(14,14,20,1)'] },
};
function getCat(c) { return CAT[c] || CAT.default; }

// ─── Helpers ────────────────────────────────────────────────────────────────
function getInstructionText(i) {
  if (!i) return '';
  if (typeof i === 'string') return i.trim();
  if (i.text) return i.text.trim();
  if (i.name) return i.name.trim();
  return '';
}

function fmtDuration(m) {
  if (!m || m <= 0) return '~30min';
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r > 0 ? `${h}h${String(r).padStart(2, '0')}` : `${h}h`;
}

function endTime(start, dur) {
  if (!start || !dur) return null;
  const [h, m] = start.split(':').map(Number);
  const t = h * 60 + m + dur;
  return `${String(Math.floor(t / 60) % 24).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

// ─── Checkbox animé ─────────────────────────────────────────────────────────
const SubTaskCheckbox = memo(function SubTaskCheckbox({ checked, color, onToggle }) {
  return (
    <motion.button
      onClick={(e) => { e.stopPropagation(); haptic.tap(); onToggle(); }}
      className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-all"
      style={{
        background: checked ? color : 'transparent',
        border: `2px solid ${checked ? color : 'rgba(255,255,255,0.15)'}`,
        boxShadow: checked ? `0 0 12px ${color}44` : 'none',
      }}
      whileTap={{ scale: 0.85 }}
    >
      <AnimatePresence>
        {checked && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
});

// ─── Action button ──────────────────────────────────────────────────────────
const ActionBtn = memo(function ActionBtn({ icon: Icon, label, color, bgColor, borderColor, onClick, fill, full }) {
  return (
    <motion.button
      onClick={onClick}
      className={`${full ? 'flex-1' : ''} py-2.5 px-4 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2`}
      style={{ background: bgColor, border: `1px solid ${borderColor}`, color }}
      whileTap={{ scale: 0.94 }}
    >
      <Icon className="w-4 h-4" fill={fill ? 'currentColor' : 'none'} />
      {label && <span>{label}</span>}
    </motion.button>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export const TaskDetailSheet = memo(function TaskDetailSheet({
  task, isOpen, isCompleted, onClose, onComplete, onStartFocus, onDelete,
  onEdit, onViewInWeek,
  onSetTimer, onSetReminder, onPlayMusic, onOpenDeviceActions,
  // Subtask interaction
  isSubTaskCompleted, toggleSubTask, getCompletedCount,
  weekOffset, currentDayIndex,
}) {
  if (!task) return null;

  const cat = getCat(task.category);
  const et = endTime(task.startTime || task.time, task.duration);
  const instructions = (task.instructions || []).filter(i => getInstructionText(i));
  const hasInstructions = instructions.length > 0;
  const completedSubs = hasInstructions && getCompletedCount
    ? getCompletedCount(weekOffset || 0, currentDayIndex || 0, task.id, instructions.length)
    : 0;
  const isCustomTask = task.id?.startsWith('custom');
  const progressPct = hasInstructions ? Math.round((completedSubs / instructions.length) * 100) : 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
            style={{ zIndex: Z_INDEX.MODAL_BACKDROP }}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            className="fixed bottom-0 left-0 right-0 max-h-[95svh] overflow-y-auto"
            style={{ zIndex: Z_INDEX.MOOD_CHECK_IN }}
          >
            <div className="rounded-t-3xl overflow-hidden"
              style={{
                background: `linear-gradient(180deg, ${cat.grad[0]} 0%, ${cat.grad[1]} 30%)`,
                boxShadow: `0 -8px 40px ${cat.glow}, 0 -2px 0 ${cat.border}`,
              }}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.12)' }} />
              </div>

              {/* ── Header ───────────────────────────────────────────── */}
              <div className="px-5 pb-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Badges */}
                    <div className="flex items-center gap-1.5 mb-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                        style={{ color: cat.text, background: `${cat.dot}18`, border: `1px solid ${cat.dot}25` }}>
                        {cat.emoji} {cat.label}
                      </span>
                      {task.isImported && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full"
                          style={{ color: 'rgba(96,165,250,0.8)', background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.18)' }}>
                          📅 Google
                        </span>
                      )}
                      {task.isAIGenerated && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full"
                          style={{ color: 'rgba(167,139,250,0.8)', background: 'rgba(139,92,246,0.10)', border: '1px solid rgba(139,92,246,0.18)' }}>
                          <Sparkles className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" /> IA
                        </span>
                      )}
                      {isCompleted && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full"
                          style={{ color: '#34d399', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.20)' }}>
                          ✓ Fait
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h2 className="text-[22px] font-bold leading-tight"
                      style={{
                        color: isCompleted ? 'rgba(255,255,255,0.30)' : 'rgba(255,255,255,0.92)',
                        textDecoration: isCompleted ? 'line-through' : 'none',
                      }}>
                      {task.task}
                    </h2>

                    {/* Description */}
                    {task.desc && !task.desc.startsWith('Jusqu') && (
                      <p className="text-[12px] mt-1 line-clamp-2" style={{ color: 'rgba(255,255,255,0.30)' }}>
                        {task.desc}
                      </p>
                    )}
                  </div>

                  <motion.button onClick={onClose} whileTap={{ scale: 0.85 }}
                    className="p-2 rounded-xl mt-1"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <X className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.35)' }} />
                  </motion.button>
                </div>

                {/* Time + Duration pills */}
                <div className="flex items-center gap-2 mt-3.5">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <Clock className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.30)' }} />
                    <span className="text-[13px] font-mono" style={{ color: 'rgba(255,255,255,0.60)' }}>
                      {task.startTime || task.time || '—'}{et ? ` → ${et}` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <Target className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.30)' }} />
                    <span className="text-[13px]" style={{ color: 'rgba(255,255,255,0.60)' }}>
                      {fmtDuration(task.duration)}
                    </span>
                  </div>
                  {task.points > 0 && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
                      style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.15)' }}>
                      <Zap className="w-3.5 h-3.5" style={{ color: '#fbbf24' }} />
                      <span className="text-[13px] font-bold" style={{ color: '#fbbf24' }}>+{task.points}</span>
                    </div>
                  )}
                </div>

                {/* Location */}
                {task.location && (
                  <div className="flex items-center gap-1.5 mt-2" style={{ color: 'rgba(255,255,255,0.25)' }}>
                    <MapPin className="w-3 h-3" />
                    <span className="text-[11px]">{task.location}</span>
                  </div>
                )}
              </div>

              {/* ── Subtasks ─────────────────────────────────────────── */}
              {hasInstructions && (
                <div className="px-5 pb-4">
                  {/* Progress header */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-semibold uppercase tracking-wider"
                      style={{ color: 'rgba(255,255,255,0.25)' }}>
                      Étapes
                    </span>
                    <span className="text-[11px] font-mono"
                      style={{ color: completedSubs === instructions.length ? '#34d399' : 'rgba(255,255,255,0.30)' }}>
                      {completedSubs}/{instructions.length}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="h-1 rounded-full mb-4 overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <motion.div
                      className="h-full rounded-full"
                      style={{
                        background: `linear-gradient(90deg, ${cat.dot}, ${cat.text})`,
                        boxShadow: progressPct > 0 ? `0 0 8px ${cat.dot}66` : 'none',
                      }}
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPct}%` }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    />
                  </div>

                  {/* Subtask list */}
                  <div className="space-y-1.5 max-h-[32vh] overflow-y-auto">
                    {instructions.map((instruction, idx) => {
                      const text = getInstructionText(instruction);
                      const checked = isSubTaskCompleted
                        ? isSubTaskCompleted(weekOffset || 0, currentDayIndex || 0, task.id, idx)
                        : false;

                      return (
                        <motion.div
                          key={idx}
                          layout
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all"
                          style={{
                            background: checked ? `${cat.dot}0a` : 'rgba(255,255,255,0.025)',
                            border: `1px solid ${checked ? `${cat.dot}22` : 'rgba(255,255,255,0.05)'}`,
                          }}
                        >
                          <SubTaskCheckbox
                            checked={checked}
                            color={cat.dot}
                            onToggle={() => toggleSubTask?.(weekOffset || 0, currentDayIndex || 0, task.id, idx)}
                          />
                          <span className="text-[13px] leading-snug flex-1"
                            style={{
                              color: checked ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.70)',
                              textDecoration: checked ? 'line-through' : 'none',
                            }}>
                            {text}
                          </span>
                          <span className="text-[10px] font-mono flex-shrink-0"
                            style={{ color: 'rgba(255,255,255,0.12)' }}>
                            {idx + 1}
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* All done celebration */}
                  <AnimatePresence>
                    {completedSubs === instructions.length && instructions.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center py-3 mt-2 rounded-xl"
                        style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)' }}>
                        <span className="text-[12px] font-semibold" style={{ color: '#34d399' }}>
                          🎉 Toutes les étapes terminées !
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* ── No subtasks ───────────────────────────────────────── */}
              {!hasInstructions && (
                <div className="px-5 pb-4">
                  <div className="text-center py-5 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <p className="text-[12px]" style={{ color: 'rgba(255,255,255,0.18)' }}>
                      Pas de sous-tâches
                    </p>
                  </div>
                </div>
              )}

              {/* ── Actions ───────────────────────────────────────────── */}
              <div className="px-5 pb-6 pt-2 space-y-2.5"
                style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>

                {/* Primary actions */}
                {!isCompleted ? (
                  <div className="flex gap-2.5">
                    <motion.button
                      onClick={() => { haptic.heavy(); onComplete?.(task); onClose(); }}
                      className="flex-1 py-3.5 rounded-2xl font-bold text-[15px] flex items-center justify-center gap-2"
                      style={{
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        color: 'white',
                        boxShadow: '0 4px 20px rgba(16,185,129,0.25)',
                      }}
                      whileTap={{ scale: 0.96 }}
                    >
                      <Check className="w-5 h-5" strokeWidth={3} />
                      Terminé
                    </motion.button>

                    {onStartFocus && (
                      <motion.button
                        onClick={() => { haptic.medium(); onStartFocus(task); onClose(); }}
                        className="py-3.5 px-6 rounded-2xl font-semibold text-[14px] flex items-center justify-center gap-2"
                        style={{
                          background: 'rgba(139,92,246,0.14)',
                          border: '1px solid rgba(139,92,246,0.25)',
                          color: '#a78bfa',
                        }}
                        whileTap={{ scale: 0.96 }}
                      >
                        <Play className="w-4.5 h-4.5" fill="currentColor" />
                        Focus
                      </motion.button>
                    )}
                  </div>
                ) : (
                  <motion.button
                    onClick={() => { onComplete?.(task); onClose(); }}
                    className="w-full py-3 rounded-2xl text-[14px] font-medium"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.40)' }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Marquer non terminé
                  </motion.button>
                )}

                {/* Quick actions */}
                {!isCompleted && (onSetTimer || onSetReminder || onPlayMusic) && (
                  <div className="flex gap-2">
                    {onSetTimer && task.duration && (
                      <ActionBtn icon={Timer} label={`${task.duration}min`}
                        color="rgba(245,158,11,0.8)" bgColor="rgba(245,158,11,0.08)" borderColor="rgba(245,158,11,0.15)"
                        onClick={() => { onSetTimer(task.duration, task.task); onClose(); }} full />
                    )}
                    {onSetReminder && (
                      <ActionBtn icon={Bell} label="Rappel"
                        color="rgba(96,165,250,0.8)" bgColor="rgba(59,130,246,0.08)" borderColor="rgba(59,130,246,0.15)"
                        onClick={() => { onSetReminder(task); onClose(); }} full />
                    )}
                    {onPlayMusic && (
                      <ActionBtn icon={Music}
                        color="rgba(244,114,182,0.8)" bgColor="rgba(236,72,153,0.08)" borderColor="rgba(236,72,153,0.15)"
                        onClick={() => onPlayMusic('lofi')} />
                    )}
                    {onOpenDeviceActions && (
                      <ActionBtn icon={Volume2}
                        color="rgba(255,255,255,0.30)" bgColor="rgba(255,255,255,0.04)" borderColor="rgba(255,255,255,0.07)"
                        onClick={onOpenDeviceActions} />
                    )}
                  </div>
                )}

                {/* Secondary */}
                <div className="flex gap-2">
                  {task.isAgendaEvent && onViewInWeek && (
                    <ActionBtn icon={Calendar} label="Semaine"
                      color="rgba(255,255,255,0.40)" bgColor="rgba(255,255,255,0.04)" borderColor="rgba(255,255,255,0.06)"
                      onClick={() => { onViewInWeek(); onClose(); }} full />
                  )}
                  {isCustomTask && onDelete && (
                    <ActionBtn icon={Trash2}
                      color="rgba(239,68,68,0.7)" bgColor="rgba(239,68,68,0.06)" borderColor="rgba(239,68,68,0.12)"
                      onClick={() => { onDelete(task.id); onClose(); }} />
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
});

export default TaskDetailSheet;
